// React bindings for the i18n layer: a provider holding the active language and
// memoized `Intl` formatters, plus hooks consumers use.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Anchor } from '@mantine/core';
import {
  readLanguage,
  resolveInitialLanguage,
  saveLanguage,
  type LanguagePreference,
} from '@/lib/preferences';
import { translate, type InterpolationValues } from './messages';

/** Bound formatters for the active language. */
export interface Formatters {
  /** Whole-number formatting (no unit; units are shown separately). */
  number: (value: number) => string;
  /** A numeric reading: at most two decimals, trailing zeros dropped. */
  reading: (value: number) => string;
  /** Timestamp for the selected locale, in the viewer's timezone. */
  dateTime: (value: string | number | Date) => string;
  /** Relative time (for example "3 minutes ago"). */
  relativeTime: (value: string | number | Date) => string;
  /** Duration in minutes, localized. */
  minutes: (value: number) => string;
}

interface I18nContextValue {
  language: LanguagePreference;
  setLanguage: (language: LanguagePreference) => void;
  t: (key: string, values?: InterpolationValues) => string;
  tNode: (key: string, values?: InterpolationValues) => ReactNode;
  format: Formatters;
}

const I18nContext = createContext<I18nContextValue | null>(null);

// Formatters are pure per language, so a module-level cache is safe and avoids
// rebuilding them on every render.
const formatterCache = new Map<LanguagePreference, Formatters>();

function buildFormatters(language: LanguagePreference): Formatters {
  const numberFormat = new Intl.NumberFormat(language);
  const readingFormat = new Intl.NumberFormat(language, { maximumFractionDigits: 2 });
  const dateTimeFormat = new Intl.DateTimeFormat(language, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
  const relativeFormat = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  const minutesFormat = new Intl.NumberFormat(language, {
    style: 'unit',
    unit: 'minute',
    unitDisplay: 'long',
  });

  return {
    number: (value) => numberFormat.format(value),
    reading: (value) => readingFormat.format(value),
    dateTime: (value) => {
      const date = value instanceof Date ? value : new Date(value);
      return Number.isNaN(date.getTime()) ? '' : dateTimeFormat.format(date);
    },
    relativeTime: (value) => {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const deltaSeconds = Math.round((date.getTime() - Date.now()) / 1000);
      const thresholds: Array<[Intl.RelativeTimeFormatUnit, number]> = [
        ['second', 60],
        ['minute', 60],
        ['hour', 24],
        ['day', 7],
        ['week', 4.348],
        ['month', 12],
        ['year', Number.POSITIVE_INFINITY],
      ];
      let remaining = deltaSeconds;
      for (const [unit, span] of thresholds) {
        if (Math.abs(remaining) < span) {
          return relativeFormat.format(Math.round(remaining), unit);
        }
        remaining /= span;
      }
      return relativeFormat.format(Math.round(remaining), 'year');
    },
    minutes: (value) => minutesFormat.format(value),
  };
}

export function I18nProvider({ children }: { children: ReactNode }): ReactNode {
  const [language, setLanguageState] = useState<LanguagePreference>(() => resolveInitialLanguage());

  const format = useMemo(() => {
    const cached = formatterCache.get(language);
    if (cached) return cached;
    const built = buildFormatters(language);
    formatterCache.set(language, built);
    return built;
  }, [language]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((next: LanguagePreference) => {
    setLanguageState(next);
    saveLanguage(next);
  }, []);

  const t = useCallback(
    (key: string, values?: InterpolationValues) => translate(language, key, values),
    [language],
  );

  const tNode = useCallback(
    (key: string, values?: InterpolationValues) => renderLinks(translate(language, key, values)),
    [language],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ language, setLanguage, t, tNode, format }),
    [language, setLanguage, t, tNode, format],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// A catalog message may mark a phrase that should be a link: the URL lives in
// the message so a translation controls where the sentence breaks, and the URL
// is written once per language rather than assembled in a component.
const LINK = /<link>(.*?)<\/link>/g;

/** Security-relevant: `javascript:` in a catalogue would run in the app. */
const SAFE_SCHEME = /^(https?:\/\/|\/)/;

/**
 * Render a message as React nodes, turning `<link>url</link>` into anchors.
 *
 * The message stays the source of truth for word order, so a translation can
 * put the link anywhere. The URL is validated here rather than trusted: a
 * catalog is code, but a mistake in one should not become an executable link.
 */
function renderLinks(message: string): ReactNode {
  if (!message.includes('<link>')) return message;
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  LINK.lastIndex = 0;
  while ((match = LINK.exec(message)) !== null) {
    const [whole, url] = match;
    if (match.index > cursor) nodes.push(message.slice(cursor, match.index));
    const href = url ?? '';
    if (SAFE_SCHEME.test(href)) {
      nodes.push(
        <Anchor key={nodes.length} href={href} target="_blank" rel="noreferrer">
          {href}
        </Anchor>,
      );
    } else {
      // Refuse to link: show the text rather than dropping the phrase.
      nodes.push(href);
    }
    cursor = match.index + whole.length;
  }
  if (cursor < message.length) nodes.push(message.slice(cursor));
  return nodes;
}

/** Access translations and formatters. Throws outside the provider. */
export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error('useI18n must be used inside I18nProvider');
  }
  return value;
}

/** Convenience hook when only the translate function is needed. */
export function useTranslation(): (key: string, values?: InterpolationValues) => string {
  return useI18n().t;
}

/** Translate and render, turning `<link>` markup into anchors. */
export function useTranslationNode(): (key: string, values?: InterpolationValues) => ReactNode {
  return useI18n().tNode;
}

/** Report whether the user currently has a saved language preference. */
export function hasSavedLanguage(): boolean {
  return readLanguage() !== null;
}
